import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { MapPin, Phone, Mail, Clock, ExternalLink } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { buildBreadcrumbLd } from "../components/Breadcrumbs";
import { findPhoto } from "../lib/photos";
import { useSeo } from "../lib/seo";
import { BOOKING_URL, PHONE_TEL, PHONE_DISPLAY } from "../components/Header";
import { GoogleG, StarRow } from "../components/GoogleReviews";
import { GOOGLE_READ_REVIEWS_URL, GOOGLE_WRITE_REVIEW_URL } from "../lib/reviews";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [photos, setPhotos] = useState([]);
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/photos`).then((r) => {
      if (alive) setPhotos(r.data.photos || []);
    });
    return () => { alive = false; };
  }, []);

  const hero = useMemo(
    () => findPhoto(photos, "westbridge-inn-suites-centerville-iowa-exterior-signage"),
    [photos],
  );

  const canonical = "https://westbridgehotels.com/contact";
  useSeo({
    title: "Contact & Directions — Westbridge Inn & Suites, Centerville, Iowa",
    description:
      "Call (641) 437-7272, email Centerville@westbridgehotels.com, or find us at 117 N 18th St, Centerville, IA. Directions from Ottumwa, Des Moines, and Hwy 2/Hwy 5.",
    canonical,
    image: hero ? `${window.location.origin}${hero.jpg}` : undefined,
    jsonLd: [buildBreadcrumbLd([{ label: "Contact", to: "/contact" }])],
  });

  return (
    <main id="main" data-testid="contact-page">
      <PageHero
        eyebrow="Contact us"
        title="A real person. A real address. A real phone number."
        intro="No form to fill out. Call the front desk, send an email, or pull up to 117 N 18th Street and knock — we'll be there."
        photo={hero}
        breadcrumbs={[{ label: "Contact", to: "/contact" }]}
        testId="contact-hero"
        compact
      />

      <section className="bg-cream py-16 md:py-24">
        <div className="container-app grid md:grid-cols-12 gap-10 md:gap-14">
          <div className="md:col-span-5 space-y-8" data-testid="contact-info">
            <ContactRow
              icon={Phone}
              label="Front desk (24 hours)"
              lines={[
                <a key="tel" href={`tel:${PHONE_TEL}`} className="hover:text-brass" data-testid="contact-phone-link">
                  {PHONE_DISPLAY}
                </a>,
              ]}
            />
            <ContactRow
              icon={Mail}
              label="Email the front desk"
              lines={[
                <a key="em" href="mailto:Centerville@westbridgehotels.com" className="hover:text-brass break-all" data-testid="contact-email-link">
                  Centerville@westbridgehotels.com
                </a>,
                <span key="ada" className="text-sm text-ink/70">Accessibility questions: <a href="mailto:info@westbridgehotels.com" className="brass-underline" data-testid="contact-ada-email-link">info@westbridgehotels.com</a></span>,
              ]}
            />
            <ContactRow
              icon={MapPin}
              label="Address"
              lines={[
                <a
                  key="addr"
                  href="https://maps.google.com/?q=117+N+18th+St,+Centerville,+IA+52544"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-brass"
                  data-testid="contact-directions-link"
                >
                  117 N 18th St, Centerville, IA 52544
                </a>,
              ]}
            />
            <ContactRow
              icon={Clock}
              label="Check-in / check-out"
              lines={["Check-in from 3 PM", "Check-out by 11 AM", "Late arrivals welcome — call ahead"]}
            />

            <div className="pt-4">
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary text-sm"
                data-testid="contact-book-now"
              >
                Book Direct <ExternalLink size={14} />
              </a>
            </div>
          </div>

          <div className="md:col-span-7">
            <div className="aspect-[4/3] border border-tan overflow-hidden">
              <iframe
                title="Map to Westbridge Inn & Suites"
                className="w-full h-full block"
                style={{ filter: "grayscale(0.3) contrast(1.05)" }}
                src="https://www.google.com/maps?q=117%20N%2018th%20St%2C%20Centerville%2C%20IA%2052544&t=m&z=14&output=embed"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="contact-map"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Directions */}
      <section className="bg-cream-100 py-16 md:py-24 paper-grain" data-testid="contact-directions">
        <div className="container-app relative">
          <div className="max-w-xl mb-10">
            <p className="eyebrow mb-3">Directions</p>
            <h2 className="font-display font-light text-3xl md:text-5xl text-forest-900 tracking-tight leading-[1.05]">
              A few turns from anywhere in Iowa.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            <DirectionCard
              from="From Des Moines"
              body="Take Hwy 5 South about 90 miles to Centerville. Hwy 5 becomes N 18th Street through town — we're a quarter mile past the historic square on the right."
            />
            <DirectionCard
              from="From Ottumwa"
              body="Hwy 63 South to Hwy 2 West about 30 miles. Hwy 2 becomes Drake Ave, then turn right on N 18th Street. We're on the right."
            />
            <DirectionCard
              from="From Kansas City"
              body="I-35 North to Hwy 2 East about 90 miles. Continue on Hwy 2 (Drake Ave) through Centerville, then left on N 18th Street."
            />
            <DirectionCard
              from="From Rathbun Lake"
              body="Take Hwy 142 East / 495th Street to Hwy 5 South about 12 miles. Hwy 5 becomes N 18th Street through town. About 15 minutes total."
            />
            <DirectionCard
              from="From Honey Creek Resort"
              body="Take Hwy J5T East to Hwy 5 South about 18 miles. About 20 minutes total. Great for weekend rain-plans."
            />
            <DirectionCard
              from="From Chicago"
              body="I-80 West to I-35 South at Des Moines, then follow the Des Moines directions above. About 6 hours total from downtown Chicago."
            />
          </div>
        </div>
      </section>

      {/* Google reviews CTA */}
      <section className="bg-cream-100 border-y border-tan/60 py-16 md:py-20 paper-grain">
        <div className="container-app grid md:grid-cols-12 gap-10 md:gap-14 items-start">
          <div className="md:col-span-6">
            <div className="flex items-center gap-3 mb-4">
              <GoogleG size={24} />
              <p className="eyebrow text-brass">Reviews on Google</p>
            </div>
            <h2 className="font-display font-light text-3xl md:text-4xl text-forest-900 leading-tight tracking-tight">
              Stayed with us? A Google review helps a small hotel more than you&rsquo;d think.
            </h2>
            <p className="mt-5 text-ink/75 text-lg leading-relaxed">
              We&rsquo;re independent — no chain, no marketing budget, no franchise reviews to hide behind. If we did right by you, an honest paragraph on Google is the biggest gift you can give the next traveler trying to decide.
            </p>
          </div>
          <div className="md:col-span-6 flex flex-col gap-4">
            <div className="border border-tan bg-cream p-6 md:p-8">
              <StarRow count={5} size={18} className="text-brass mb-3" />
              <p className="text-xs uppercase tracking-[0.18em] text-ink/60">Two clicks. No account setup.</p>
              <a
                href={GOOGLE_WRITE_REVIEW_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary mt-5 w-full justify-center"
                data-testid="contact-review-write"
              >
                Write a Google review <ExternalLink size={14} />
              </a>
              <a
                href={GOOGLE_READ_REVIEWS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-ghost-dark mt-3 w-full justify-center text-sm"
                data-testid="contact-review-read"
              >
                Read what other guests wrote
              </a>
            </div>
            <p className="text-xs text-ink/55 leading-relaxed">
              Prefer email? Send us your thoughts at{" "}
              <a
                href="mailto:Centerville@westbridgehotels.com"
                className="brass-underline text-forest-900"
                data-testid="contact-review-email"
              >
                Centerville@westbridgehotels.com
              </a>{" "}
              — we&rsquo;ll pass the good ones to the team by name.
            </p>
          </div>
        </div>
      </section>

      {/* Reassurance */}
      <section className="bg-forest-900 text-cream py-14 md:py-20">
        <div className="container-app max-w-3xl">
          <p className="eyebrow text-brass-300 mb-3">A quick note</p>
          <h2 className="font-display text-2xl md:text-3xl leading-tight">
            No web forms. On purpose.
          </h2>
          <p className="mt-4 text-cream/85 text-lg leading-relaxed">
            You'll notice there's no "contact form" on this page. That's intentional. Every reasonable question can be answered by the front desk in under two minutes on a phone call — faster than any form submission, and better because a real person can act on it. Call us any time.
          </p>
        </div>
      </section>
    </main>
  );
}

function ContactRow({ icon: Icon, label, lines }) {
  return (
    <div>
      <div className="flex items-center gap-3 mb-2 text-brass">
        <Icon size={18} strokeWidth={1.5} />
        <p className="eyebrow">{label}</p>
      </div>
      <div className="text-forest-900 text-lg space-y-1">
        {lines.map((l, i) => <div key={i}>{l}</div>)}
      </div>
    </div>
  );
}

function DirectionCard({ from, body }) {
  return (
    <article className="bg-cream border border-tan p-6 md:p-7">
      <p className="eyebrow text-brass mb-3">{from}</p>
      <p className="text-ink/85 leading-relaxed text-[0.98rem]">{body}</p>
    </article>
  );
}
